import { useState, useEffect, useRef } from "react";

// ─── Theme (Maghil Design System) ───────────────────────────────────────────
const T = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF",
  cardBorder: "#E2E5EC", hover: "#F0F1F5",
  accent: "#E52333", accentSoft: "rgba(229,35,51,0.08)",
  green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706", orangeSoft: "rgba(217,119,6,0.08)",
  t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// ─── Date Presets (LAND-6) ──────────────────────────────────────────────────
const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "last_7", label: "Last 7 Days" },
  { key: "this_month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

const getPresetLabel = (preset, customRange) => {
  if (preset === "custom" && customRange) {
    const fmtD = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmtD(customRange.start)} – ${fmtD(customRange.end)}`;
  }
  return DATE_PRESETS.find((p) => p.key === preset)?.label || preset;
};

const getComparisonLabel = (preset) => {
  const map = {
    today: "vs yesterday",
    yesterday: "vs day before",
    this_week: "vs last week",
    last_7: "vs prior 7 days",
    this_month: "vs last month",
    custom: "vs prior period",
  };
  return map[preset] || "";
};

// ─── Mock Data with multi-period KPIs ───────────────────────────────────────
const OUTLET_DATA = {
  o1: {
    name: "Jersey City — Newark Ave", brand: "A2B", logo: null,
    periods: {
      today:      { netSales: 6240, netSalesPrev: 5100, orders: 187, ordersPrev: 162 },
      yesterday:  { netSales: 5840, netSalesPrev: 5600, orders: 171, ordersPrev: 168 },
      this_week:  { netSales: 38200, netSalesPrev: 35600, orders: 1140, ordersPrev: 1060 },
      last_7:     { netSales: 41500, netSalesPrev: 38900, orders: 1240, ordersPrev: 1150 },
      this_month: { netSales: 162400, netSalesPrev: 155800, orders: 4870, ordersPrev: 4680 },
    },
  },
  o2: {
    name: "Edison — Oak Tree Rd", brand: "A2B", logo: null,
    periods: {
      today:      { netSales: 4810, netSalesPrev: 4950, orders: 143, ordersPrev: 151 },
      yesterday:  { netSales: 4920, netSalesPrev: 4700, orders: 148, ordersPrev: 140 },
      this_week:  { netSales: 31400, netSalesPrev: 29800, orders: 940, ordersPrev: 890 },
      last_7:     { netSales: 33600, netSalesPrev: 31200, orders: 1005, ordersPrev: 935 },
      this_month: { netSales: 138600, netSalesPrev: 132000, orders: 4160, ordersPrev: 3960 },
    },
  },
  o3: {
    name: "Parsippany — Rt 46", brand: "A2B", logo: null,
    periods: {
      today:      { netSales: 3920, netSalesPrev: 3920, orders: 118, ordersPrev: 118 },
      yesterday:  { netSales: 4100, netSalesPrev: 3850, orders: 123, ordersPrev: 115 },
      this_week:  { netSales: 42100, netSalesPrev: 35400, orders: 1260, ordersPrev: 1060 },
      last_7:     { netSales: 28200, netSalesPrev: 26100, orders: 845, ordersPrev: 780 },
      this_month: { netSales: 118400, netSalesPrev: 112000, orders: 3550, ordersPrev: 3360 },
    },
  },
  o4: {
    name: "Iselin — Green St", brand: "A2B", logo: null,
    periods: {
      today:      { netSales: 2150, netSalesPrev: 0, orders: 64, ordersPrev: 0 },
      yesterday:  { netSales: 0, netSalesPrev: null, orders: 0, ordersPrev: null },
      this_week:  { netSales: 2150, netSalesPrev: 0, orders: 64, ordersPrev: 0 },
      last_7:     { netSales: 2150, netSalesPrev: 0, orders: 64, ordersPrev: 0 },
      this_month: { netSales: 2150, netSalesPrev: 0, orders: 64, ordersPrev: 0 },
    },
  },
  o5: {
    name: "Downtown Austin", brand: "Maghil Eats", logo: null,
    periods: {
      today:      { netSales: 5380, netSalesPrev: 4200, orders: 156, ordersPrev: 130 },
      yesterday:  { netSales: 5100, netSalesPrev: 4800, orders: 148, ordersPrev: 139 },
      this_week:  { netSales: 34600, netSalesPrev: 31200, orders: 1004, ordersPrev: 905 },
      last_7:     { netSales: 37100, netSalesPrev: 33800, orders: 1076, ordersPrev: 980 },
      this_month: { netSales: 149200, netSalesPrev: 140600, orders: 4330, ordersPrev: 4080 },
    },
  },
  o6: {
    name: "South Congress", brand: "Maghil Eats", logo: null, dataError: true,
    periods: {
      today:      { netSales: 3100, netSalesPrev: 3600, orders: 98, ordersPrev: 112 },
      yesterday:  { netSales: 3400, netSalesPrev: 3200, orders: 105, ordersPrev: 98 },
      this_week:  { netSales: 20800, netSalesPrev: 19400, orders: 640, ordersPrev: 597 },
      last_7:     { netSales: 22300, netSalesPrev: 21000, orders: 686, ordersPrev: 646 },
      this_month: { netSales: 93200, netSalesPrev: 88400, orders: 2870, ordersPrev: 2720 },
    },
  },
};

const MOCK_USER = {
  name: "Ravi Shankar",
  role: "Franchise Owner",
  outlets: Object.entries(OUTLET_DATA).map(([id, o]) => ({ id, name: o.name, brand: o.brand, logo: o.logo })),
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (v) => "$" + v.toLocaleString("en-US");
const pctChange = (curr, prev) => {
  if (prev == null || prev === 0) return null;
  return (((curr - prev) / prev) * 100).toFixed(1);
};
const getBrands = (outlets: any[]) => {
  const map: Record<string, any[]> = {};
  outlets.forEach((o) => { if (!map[o.brand]) map[o.brand] = []; map[o.brand].push(o); });
  return Object.entries(map)
    .map(([name, items]) => ({ name, count: items.length }))
    .sort((a, b) => a.name.localeCompare(b.name));
};
const defaultBrand = (brands) => {
  const maxCount = Math.max(...brands.map((b) => b.count));
  const top = brands.filter((b) => b.count === maxCount);
  return top.sort((a, b) => a.name.localeCompare(b.name))[0].name;
};

const getOutletKPIs = (outletId, period) => {
  const data = OUTLET_DATA[outletId];
  if (!data) return null;
  // LAND-7: Simulate partial data failure for South Congress on "yesterday"
  if (data.dataError && period === "yesterday") return { error: true };
  const p = period === "custom" ? data.periods.last_7 : data.periods[period];
  if (!p) return { netSales: 0, netSalesPrev: null, orders: 0, ordersPrev: null };
  return p;
};

// ─── GA4 Stub ───────────────────────────────────────────────────────────────
const ga4 = (event, params) => {
  const base = { user_name: MOCK_USER.name, user_role: MOCK_USER.role, platform: "mobile", timestamp: new Date().toISOString() };
  console.log(`[GA4] ${event}`, { ...base, ...params });
};

// ─── Skeleton Card ──────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div style={{
    background: T.card, borderRadius: 14, border: `1px solid ${T.cardBorder}`,
    boxShadow: T.shadow, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 20, height: 20, borderRadius: 5, background: T.hover }} />
      <div style={{ flex: 1 }}>
        <div style={{ width: "70%", height: 12, borderRadius: 4, background: T.hover }} />
      </div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      {[0, 1].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ width: "50%", height: 8, borderRadius: 3, background: T.hover }} />
          <div style={{ width: "60%", height: 14, borderRadius: 4, background: T.hover }} />
          <div style={{ width: "40%", height: 8, borderRadius: 3, background: T.hover }} />
        </div>
      ))}
    </div>
  </div>
);

// ─── Delta Badge ────────────────────────────────────────────────────────────
const Delta = ({ value, size = 9 }) => {
  if (value === null) return <span style={{ fontSize: size, fontWeight: 500, color: T.t3 }}>N/A</span>;
  const num = parseFloat(value);
  const isPos = num > 0;
  const isZero = num === 0;
  const color = isZero ? T.t3 : isPos ? T.green : T.red;
  const bg = isZero ? "transparent" : isPos ? T.greenSoft : T.redSoft;
  const arrow = isZero ? "" : isPos ? "↑" : "↓";
  return (
    <span style={{
      fontSize: size, fontWeight: 600, color, background: bg,
      borderRadius: 4, padding: "1px 5px", display: "inline-flex", alignItems: "center", gap: 2,
    }}>
      {arrow} {Math.abs(num)}%
    </span>
  );
};

// ─── Date Filter Pills (LAND-6) ────────────────────────────────────────────
const DateFilter = ({ activePreset, customRange, onSelect, onCustom }) => (
  <div style={{
    display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2,
    WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none",
  }}>
    <style>{`.df-scroll::-webkit-scrollbar{display:none}`}</style>
    {DATE_PRESETS.map((p) => {
      const isActive = activePreset === p.key;
      const label = p.key === "custom" && customRange
        ? getPresetLabel("custom", customRange)
        : p.label;
      return (
        <div
          key={p.key}
          onClick={() => p.key === "custom" ? onCustom() : onSelect(p.key)}
          style={{
            padding: "5px 12px", borderRadius: 6, cursor: "pointer",
            fontSize: 11, fontWeight: isActive ? 600 : 400, whiteSpace: "nowrap",
            color: isActive ? T.accent : T.t2,
            background: isActive ? T.accentSoft : "transparent",
            transition: "all 0.15s ease", flexShrink: 0,
          }}
        >
          {label}
        </div>
      );
    })}
  </div>
);

// ─── Custom Date Picker Modal (LAND-6) ─────────────────────────────────────
const CustomDatePicker = ({ onApply, onClose, initial }) => {
  const today = new Date();
  const [start, setStart] = useState(initial?.start ? initial.start.toISOString().split("T")[0] : "");
  const [end, setEnd] = useState(initial?.end ? initial.end.toISOString().split("T")[0] : "");
  const maxDate = today.toISOString().split("T")[0];
  const isValid = start && end && new Date(start) <= new Date(end) && new Date(end) <= today;

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 50,
      background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "flex-end",
    }} onClick={onClose}>
      <div style={{
        width: "100%", background: T.surface, borderRadius: "16px 16px 0 0",
        padding: "20px 20px 32px",
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>Custom Date Range</span>
          <div onClick={onClose} style={{ cursor: "pointer", padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path d="M4 4L12 12M12 4L4 12" stroke={T.t3} strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>Start Date</label>
            <input type="date" value={start} max={maxDate} onChange={(e) => setStart(e.target.value)}
              style={{
                padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.cardBorder}`,
                fontSize: 13, fontFamily: T.font, color: T.t1, background: T.bg, outline: "none",
              }}
            />
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>End Date</label>
            <input type="date" value={end} max={maxDate} onChange={(e) => setEnd(e.target.value)}
              style={{
                padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.cardBorder}`,
                fontSize: 13, fontFamily: T.font, color: T.t1, background: T.bg, outline: "none",
              }}
            />
          </div>
        </div>
        <button disabled={!isValid}
          onClick={() => isValid && onApply({ start: new Date(start), end: new Date(end) })}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
            background: isValid ? T.accent : T.hover, color: isValid ? "#fff" : T.t3,
            fontSize: 14, fontWeight: 600, fontFamily: T.font, cursor: isValid ? "pointer" : "default",
          }}
        >Apply Range</button>
      </div>
    </div>
  );
};

// ─── Bottom Navigation (LAND-8) ─────────────────────────────────────────────
const TAB_ICONS = {
  grid: (c) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.8"/><rect x="14" y="3" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.8"/><rect x="3" y="14" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.8"/><rect x="14" y="14" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.8"/></svg>,
  reports: (c) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke={c} strokeWidth="1.8" strokeLinecap="round"/><rect x="9" y="3" width="6" height="4" rx="1" stroke={c} strokeWidth="1.8"/><path d="M9 12h6M9 16h4" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  ai: (c) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 3L3 9l9 6 9-6-9-6z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/><path d="M3 15l9 6 9-6" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
};

const BottomNav = ({ mode, activeTab, onTabChange }) => {
  // mode: "overview" (landing page) or "snapshot" (inside outlet)
  const firstLabel = mode === "overview" ? "Overview" : "Snapshot";
  const tabs = [
    { label: firstLabel, icon: TAB_ICONS.grid },
    { label: "Reports", icon: TAB_ICONS.reports },
    { label: "Maghil AI", icon: TAB_ICONS.ai },
  ];

  return (
    <div style={{
      height: 72, background: T.surface, borderTop: `1px solid ${T.divider}`,
      display: "flex", justifyContent: "space-around", alignItems: "flex-start",
      paddingTop: 8, paddingBottom: 8, flexShrink: 0,
    }}>
      {tabs.map((tab, i) => {
        const active = i === activeTab;
        const color = active ? T.accent : T.t3;
        return (
          <div key={i} onClick={() => onTabChange(i)} style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
            cursor: "pointer", padding: "4px 14px", position: "relative",
          }}>
            {tab.icon(color)}
            <span style={{ fontSize: 9.5, fontWeight: active ? 700 : 400, color }}>{tab.label}</span>
            {active && <div style={{
              width: 4, height: 4, borderRadius: 2, background: T.accent,
              position: "absolute", bottom: -2, boxShadow: `0 0 6px ${T.accent}`,
            }} />}
          </div>
        );
      })}
    </div>
  );
};

// ─── Reports Prompt (no outlet selected — LAND-8) ───────────────────────────
const ReportsPrompt = ({ onBack }) => (
  <div style={{
    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "0 32px", gap: 16, textAlign: "center",
  }}>
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke={T.t3} strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="9" y="3" width="6" height="4" rx="1" stroke={T.t3} strokeWidth="1.5"/>
      <path d="M9 12h6M9 16h4" stroke={T.t3} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
    <span style={{ fontSize: 15, fontWeight: 600, color: T.t1 }}>Select an outlet to view reports</span>
    <span style={{ fontSize: 12, color: T.t3 }}>Tap an outlet card on the Overview page to access its reports.</span>
    <div onClick={onBack} style={{
      padding: "8px 20px", borderRadius: 8, background: T.accent,
      color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 4,
    }}>Back to Overview</div>
  </div>
);

// ─── Outlet Card (LAND-3 + LAND-6 period + LAND-7 error state) ─────────────
const OutletCard = ({ outlet, kpis, onTap, compLabel }) => {
  const initial = outlet.name.charAt(0).toUpperCase();
  const hasError = kpis?.error;

  return (
    <div
      onClick={() => onTap(outlet)}
      style={{
        background: T.card, borderRadius: 14,
        border: `1px solid rgba(229,35,51,0.20)`,
        boxShadow: T.shadow, padding: "14px 16px",
        cursor: "pointer", position: "relative",
        transition: "transform 0.12s ease",
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.985)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      {/* Drill arrow */}
      <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 16, right: 14 }}>
        <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {/* Header: logo 20px (50% of 40px) + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: hasError ? 0 : 14 }}>
        {outlet.logo ? (
          <img src={outlet.logo} alt="" style={{ width: 20, height: 20, borderRadius: 5, objectFit: "cover" }} />
        ) : (
          <div style={{
            width: 20, height: 20, borderRadius: 5,
            background: `linear-gradient(135deg, ${T.accent}, #FB5463)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 9, fontWeight: 700,
          }}>{initial}</div>
        )}
        <span style={{ fontSize: 13, fontWeight: 600, color: T.t1, flex: 1, paddingRight: 16 }}>
          {outlet.name}
        </span>
      </div>

      {/* LAND-7: Partial data failure */}
      {hasError ? (
        <div style={{
          marginTop: 12, padding: "12px 0", display: "flex", alignItems: "center",
          justifyContent: "center", gap: 6,
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14">
            <circle cx="7" cy="7" r="6" fill="none" stroke={T.orange} strokeWidth="1.3" />
            <path d="M7 4.5V7.5M7 9V9.5" stroke={T.orange} strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 11, color: T.orange, fontWeight: 500 }}>Data unavailable</span>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>Net Sales</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>{fmt(kpis.netSales)}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Delta value={pctChange(kpis.netSales, kpis.netSalesPrev)} />
              {compLabel && <span style={{ fontSize: 7, color: T.t3, fontWeight: 400 }}>{compLabel}</span>}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>Orders</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>{kpis.orders.toLocaleString("en-US")}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Delta value={pctChange(kpis.orders, kpis.ordersPrev)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Snapshot Stub (receives date from landing — LAND-6, LAND-8 bottom nav) ─
const SnapshotScreen = ({ outlet, onBack, datePreset, customRange }) => {
  const [activeTab, setActiveTab] = useState(0);
  const kpis = getOutletKPIs(outlet.id, datePreset);
  const periodLabel = getPresetLabel(datePreset, customRange);
  const compLabel = getComparisonLabel(datePreset);
  const safeKpis = kpis?.error ? null : kpis;

  // LAND-8: Reports tab (index 1) inside outlet shows report list
  const reportGroups = [
    { emoji: "💰", name: "Sales", desc: "Revenue, hourly trends, order channels" },
    { emoji: "🍽️", name: "Product", desc: "Item sales, categories, discounts" },
    { emoji: "💳", name: "Payments", desc: "Tender types, reconciliation" },
    { emoji: "👥", name: "Labour", desc: "Staff performance, scheduling" },
    { emoji: "🧑‍🤝‍🧑", name: "Customers", desc: "Guest profiles, retention" },
    { emoji: "📋", name: "Check-in", desc: "Check-in analytics, wait times" },
    { emoji: "📅", name: "Reservation", desc: "Booking trends, no-shows" },
    { emoji: "🎉", name: "Events", desc: "Event bookings, revenue" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: T.bg }}>
      <div style={{ height: 44, background: T.surface }} />
      <div style={{ background: T.surface, padding: "8px 20px 12px", borderBottom: `1px solid ${T.divider}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          {onBack && (
            <div onClick={() => { setActiveTab(0); onBack(); }} style={{
              width: 28, height: 28, borderRadius: 8, background: T.hover,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12">
                <path d="M8 2L4 6L8 10" fill="none" stroke={T.t2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          <span style={{ fontSize: 11, color: T.t3, fontWeight: 400 }}>Overview</span>
          <span style={{ fontSize: 11, color: T.t3 }}>›</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>
            {activeTab === 1 ? "Reports" : activeTab === 2 ? "Maghil AI" : "Snapshot"}
          </span>
        </div>
        <div style={{ paddingLeft: onBack ? 38 : 0, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.accent }}>{outlet.name}</span>
          <span style={{ fontSize: 9, color: T.t3 }}>•</span>
          <span style={{
            fontSize: 9, fontWeight: 500, color: T.accent,
            background: T.accentSoft, borderRadius: 4, padding: "2px 6px",
          }}>{periodLabel}</span>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 0 && (
        <div style={{ flex: 1, overflow: "auto", padding: "16px 16px 16px" }}>
          <div style={{
            background: T.card, borderRadius: 16, padding: "16px 18px",
            boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 10,
          }}>
            <span style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>
              Net Sales — {periodLabel}
            </span>
            <div style={{ fontSize: 34, fontWeight: 700, color: T.t1, letterSpacing: -1, marginTop: 4 }}>
              {safeKpis ? fmt(safeKpis.netSales) : "—"}
            </div>
            {safeKpis && (
              <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <Delta value={pctChange(safeKpis.netSales, safeKpis.netSalesPrev)} size={12} />
                <span style={{ fontSize: 10, color: T.t3 }}>{compLabel}</span>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
            {[
              { label: "Orders", value: safeKpis ? safeKpis.orders.toString() : "—", delta: safeKpis ? pctChange(safeKpis.orders, safeKpis.ordersPrev) : null },
              { label: "Avg Check", value: safeKpis ? fmt(Math.round(safeKpis.netSales / (safeKpis.orders || 1))) : "—", delta: null },
              { label: "Guests", value: safeKpis ? Math.round(safeKpis.orders * 1.8).toString() : "—", delta: null },
            ].map((k, i) => (
              <div key={i} style={{
                background: T.card, borderRadius: 12, padding: "12px 10px",
                boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`,
                display: "flex", flexDirection: "column", gap: 2,
              }}>
                <span style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{k.label}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>{k.value}</span>
                {k.delta !== null && <Delta value={k.delta} />}
              </div>
            ))}
          </div>

          {["Hourly Sales", "Top Selling Items", "Order Channels"].map((s, i) => (
            <div key={i} style={{
              background: T.card, borderRadius: 14, padding: 14,
              boxShadow: T.shadow, border: `1px solid ${T.cardBorder}`, marginBottom: 10,
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{s}</span>
              <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 11, color: T.t3 }}>Chart renders here</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LAND-8: Reports tab inside outlet — 8-item report list */}
      {activeTab === 1 && (
        <div style={{ flex: 1, overflow: "auto", padding: "12px 16px 16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {reportGroups.map((r, i) => (
              <div key={i} style={{
                background: T.card, borderRadius: 12, padding: "12px 14px",
                border: `1px solid rgba(229,35,51,0.15)`, boxShadow: T.shadow,
                display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                position: "relative",
              }}>
                <span style={{ fontSize: 18 }}>{r.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>{r.name}</div>
                  <div style={{ fontSize: 10, color: T.t3, marginTop: 1 }}>{r.desc}</div>
                </div>
                <svg width="10" height="10" viewBox="0 0 10 10" style={{ flexShrink: 0 }}>
                  <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LAND-8: Maghil AI tab */}
      {activeTab === 2 && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "0 32px", gap: 12, textAlign: "center",
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: `linear-gradient(135deg, ${T.accent}, #FB5463)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 20, fontWeight: 700,
          }}>M</div>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.t1 }}>Maghil AI</span>
          <span style={{ fontSize: 12, color: T.t3 }}>
            Ask anything about {outlet.name}'s performance, trends, or operations.
          </span>
          <div style={{
            width: "100%", padding: "10px 14px", borderRadius: 10,
            border: `1px solid ${T.cardBorder}`, background: T.bg,
            fontSize: 12, color: T.t3, textAlign: "left",
          }}>Ask Maghil AI a question…</div>
        </div>
      )}

      {/* LAND-8: Bottom nav with mode=snapshot, label = "Snapshot" */}
      <BottomNav mode="snapshot" activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};

// ─── Multi-Brand Landing Page ───────────────────────────────────────────────
const LandingPage = ({ user, onSelectOutlet, datePreset, onDateChange, customRange, onCustomRange }) => {
  const brands = getBrands(user.outlets);
  const isSingleBrand = brands.length === 1;
  const [selectedBrand, setSelectedBrand] = useState(() => defaultBrand(brands));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // LAND-8: 0=Overview, 1=Reports, 2=AI
  const pageLoadTime = useRef(Date.now());

  useEffect(() => {
    ga4("maghil_reports_page_view", {
      report_name: "Multi-Brand Landing", report_group: "Navigation", restaurant_name: selectedBrand,
    });
  }, []);

  const periodKey = datePreset === "custom" ? "custom" : datePreset;
  const filteredOutlets = user.outlets
    .filter((o) => o.brand === selectedBrand)
    .map((o) => ({ ...o, kpis: getOutletKPIs(o.id, periodKey) }))
    .sort((a, b) => {
      const aVal = a.kpis?.error ? -1 : (a.kpis?.netSales ?? 0);
      const bVal = b.kpis?.error ? -1 : (b.kpis?.netSales ?? 0);
      return bVal - aVal;
    });

  const compLabel = getComparisonLabel(datePreset);

  const handleBrandSwitch = (brandName) => {
    if (brandName === selectedBrand) { setDropdownOpen(false); return; }
    setDropdownOpen(false);
    setLoading(true);
    setApiError(false);
    ga4("maghil_reports_filter_outlet", { restaurant_name: brandName });
    setSelectedBrand(brandName);
    setTimeout(() => setLoading(false), 600);
  };

  const handleDateChange = (preset) => {
    setLoading(true);
    setApiError(false);
    onDateChange(preset);
    setTimeout(() => setLoading(false), 500);
  };

  const handleCustomApply = (range) => {
    setShowCustomPicker(false);
    setLoading(true);
    setApiError(false);
    onCustomRange(range);
    onDateChange("custom");
    setTimeout(() => setLoading(false), 500);
  };

  const handleRetry = () => {
    setLoading(true);
    setApiError(false);
    setTimeout(() => setLoading(false), 600);
  };

  const handleOutletTap = (outlet) => {
    ga4("maghil_reports_nav_click", {
      report_name: "Snapshot", source: "landing_page_card", restaurant_name: outlet.name,
    });
    ga4("maghil_reports_page_exit", {
      report_name: "Multi-Brand Landing", time_on_report_ms: Date.now() - pageLoadTime.current,
    });
    onSelectOutlet(outlet);
  };

  const selectedBrandData = brands.find((b) => b.name === selectedBrand);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: T.bg, position: "relative" }}>
      <div style={{ height: 44, background: T.surface }} />

      {/* LAND-8: Tab 0 = Overview (landing page content) */}
      {activeTab === 0 && (
        <>
          {/* ── Fixed header: title + brand selector + date filter ── */}
          <div style={{ background: T.surface, borderBottom: `1px solid ${T.divider}`, flexShrink: 0 }}>
            {/* Title */}
            <div style={{ padding: "12px 20px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: T.t1 }}>My Outlets</span>
                <div style={{
                  width: 32, height: 32, borderRadius: 16,
                  background: `linear-gradient(135deg, ${T.accent}, #FB5463)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 13, fontWeight: 600,
                }}>{user.name.split(" ").map((w) => w[0]).join("")}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 400, color: T.t3 }}>
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
              </span>
            </div>

            {/* Brand selector */}
            <div style={{ padding: "10px 20px 0", position: "relative" }}>
              {isSingleBrand ? (
                <div style={{
                  background: T.bg, borderRadius: 10, padding: "8px 12px",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6,
                      background: `linear-gradient(135deg, ${T.accent}, #FB5463)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: 10, fontWeight: 700,
                    }}>{brands[0].name.charAt(0)}</div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{brands[0].name}</span>
                  </div>
                  <span style={{ fontSize: 10, color: T.t3, fontWeight: 500 }}>
                    {brands[0].count} outlet{brands[0].count !== 1 ? "s" : ""}
                  </span>
                </div>
              ) : (
                <>
                  <div onClick={() => setDropdownOpen(!dropdownOpen)} style={{
                    background: T.bg, borderRadius: 10, padding: "8px 12px",
                    border: `1px solid ${dropdownOpen ? T.accent : "transparent"}`,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    transition: "border-color 0.15s ease",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: 6,
                        background: `linear-gradient(135deg, ${T.accent}, #FB5463)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: 10, fontWeight: 700,
                      }}>{selectedBrand.charAt(0)}</div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{selectedBrand}</span>
                        <span style={{ fontSize: 9, color: T.t3, fontWeight: 400 }}>
                          {selectedBrandData?.count} outlet{selectedBrandData?.count !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 14 14" style={{
                      transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease",
                    }}>
                      <path d="M3 5L7 9L11 5" fill="none" stroke={T.t3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>

                  {dropdownOpen && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 2px)", left: 20, right: 20,
                      background: T.card, borderRadius: 10, border: `1px solid ${T.cardBorder}`,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.10)", zIndex: 20, overflow: "hidden",
                    }}>
                      {brands.map((b, i) => {
                        const isActive = b.name === selectedBrand;
                        return (
                          <div key={b.name} onClick={() => handleBrandSwitch(b.name)} style={{
                            padding: "11px 14px",
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            cursor: "pointer",
                            background: isActive ? T.accentSoft : "transparent",
                            borderBottom: i < brands.length - 1 ? `1px solid ${T.divider}` : "none",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{
                                width: 22, height: 22, borderRadius: 6,
                                background: isActive ? `linear-gradient(135deg, ${T.accent}, #FB5463)` : T.hover,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                color: isActive ? "#fff" : T.t2, fontSize: 10, fontWeight: 600,
                              }}>{b.name.charAt(0)}</div>
                              <span style={{
                                fontSize: 12, fontWeight: isActive ? 600 : 500,
                                color: isActive ? T.accent : T.t1,
                              }}>{b.name}</span>
                            </div>
                            <span style={{ fontSize: 10, color: T.t3, fontWeight: 500 }}>
                              {b.count} outlet{b.count !== 1 ? "s" : ""}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Date filter pills — LAND-6 */}
            <div style={{ padding: "10px 20px 10px" }}>
              <DateFilter
                activePreset={datePreset}
                customRange={customRange}
                onSelect={handleDateChange}
                onCustom={() => setShowCustomPicker(true)}
              />
            </div>
          </div>

          {/* ── Scrollable card area ── */}
          <div style={{ flex: 1, overflow: "auto", padding: "10px 16px 24px" }}>
            <div style={{ marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.t2 }}>
                {loading ? "" : `${filteredOutlets.length} Outlet${filteredOutlets.length !== 1 ? "s" : ""}`}
              </span>
              {!loading && filteredOutlets.length > 0 && (
                <span style={{ fontSize: 9, color: T.t3, fontWeight: 400 }}>Sorted by net sales</span>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {loading ? (
                [0, 1, 2].map((i) => <SkeletonCard key={i} />)
              ) : apiError ? (
                <div style={{
                  background: T.card, borderRadius: 14, padding: "32px 20px",
                  border: `1px solid ${T.cardBorder}`, boxShadow: T.shadow,
                  textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
                }}>
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <circle cx="14" cy="14" r="12" fill="none" stroke={T.red} strokeWidth="1.5" />
                    <path d="M14 9V15M14 18V19" stroke={T.red} strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <span style={{ fontSize: 13, color: T.t1, fontWeight: 500 }}>Unable to load outlet data. Please try again.</span>
                  <div onClick={handleRetry} style={{
                    padding: "8px 24px", borderRadius: 8, background: T.accent,
                    color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}>Retry</div>
                </div>
              ) : filteredOutlets.length === 0 ? (
                <div style={{
                  background: T.card, borderRadius: 14, padding: "32px 16px",
                  border: `1px solid ${T.cardBorder}`, boxShadow: T.shadow, textAlign: "center",
                }}>
                  <span style={{ fontSize: 13, color: T.t3 }}>No outlets found for {selectedBrand}.</span>
                </div>
              ) : (
                filteredOutlets.map((o) => (
                  <OutletCard key={o.id} outlet={o} kpis={o.kpis} onTap={handleOutletTap} compLabel={compLabel} />
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* LAND-8: Tab 1 = Reports (no outlet selected — show prompt) */}
      {activeTab === 1 && (
        <>
          <div style={{ background: T.surface, padding: "12px 20px", borderBottom: `1px solid ${T.divider}`, flexShrink: 0 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>Reports</span>
          </div>
          <ReportsPrompt onBack={() => setActiveTab(0)} />
        </>
      )}

      {/* LAND-8: Tab 2 = Maghil AI (works without outlet context) */}
      {activeTab === 2 && (
        <>
          <div style={{ background: T.surface, padding: "12px 20px", borderBottom: `1px solid ${T.divider}`, flexShrink: 0 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>Maghil AI</span>
          </div>
          <div style={{
            flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", padding: "0 32px", gap: 12, textAlign: "center",
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: `linear-gradient(135deg, ${T.accent}, #FB5463)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 20, fontWeight: 700,
            }}>M</div>
            <span style={{ fontSize: 15, fontWeight: 600, color: T.t1 }}>Maghil AI</span>
            <span style={{ fontSize: 12, color: T.t3 }}>
              Ask about trends across your outlets, compare performance, or get recommendations.
            </span>
            <div style={{
              width: "100%", padding: "10px 14px", borderRadius: 10,
              border: `1px solid ${T.cardBorder}`, background: T.bg,
              fontSize: 12, color: T.t3, textAlign: "left",
            }}>Ask Maghil AI a question…</div>
          </div>
        </>
      )}

      {/* LAND-8: Bottom nav with mode=overview, label = "Overview" */}
      <BottomNav mode="overview" activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Custom date picker modal */}
      {showCustomPicker && (
        <CustomDatePicker
          initial={customRange}
          onApply={handleCustomApply}
          onClose={() => setShowCustomPicker(false)}
        />
      )}
    </div>
  );
};

// ─── App Shell ──────────────────────────────────────────────────────────────
export default function App() {
  const user = MOCK_USER;
  const [screen, setScreen] = useState("init");
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  // LAND-6: Date state lifted to App for persistence across all screens
  const [datePreset, setDatePreset] = useState("today");
  const [customRange, setCustomRange] = useState(null);

  useEffect(() => {
    if (user.outlets.length >= 2) setScreen("landing");
    else { setSelectedOutlet(user.outlets[0]); setScreen("snapshot"); }
  }, []);

  return (
    <div style={{
      width: 375, height: 812, margin: "0 auto",
      fontFamily: T.font, background: T.bg,
      borderRadius: 20, overflow: "hidden",
      boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
      position: "relative",
    }}>
      {screen === "init" && null}
      {screen === "landing" && (
        <LandingPage
          user={user}
          onSelectOutlet={(o) => { setSelectedOutlet(o); setScreen("snapshot"); }}
          datePreset={datePreset}
          onDateChange={setDatePreset}
          customRange={customRange}
          onCustomRange={setCustomRange}
        />
      )}
      {screen === "snapshot" && selectedOutlet && (
        <SnapshotScreen
          outlet={selectedOutlet}
          onBack={user.outlets.length >= 2 ? () => setScreen("landing") : undefined}
          datePreset={datePreset}
          customRange={customRange}
        />
      )}
    </div>
  );
}
